import configs from "../utils/configs";
import { getAuthHeaders } from "../utils/getToken";
import { proxiedUrlFor } from "../utils/media-url-utils";

const API_BASE = `${configs.PERSISTENCE_API_URL}/avatar-builder`;

export const AvatarBuilderApi = {
  async getHeaders(url, method = "GET") {
    return {
      "Content-Type": "application/json",
      ...(await getAuthHeaders(url, method))
    };
  },

  /**
   * Fetch base avatar descriptor for an avatar type
   * @param {string} avatarType - "male", "female", etc.
   * @returns {Promise<Object>} Base avatar descriptor
   */
  async fetchBase(avatarType) {
    const url = `${API_BASE}/presets/base/${avatarType}`;
    const res = await fetch(url, {
      headers: await this.getHeaders(url, "GET")
    });
    const data = await res.json();
    return data.data;
  },

  /**
   * Fetch all preset templates
   * @returns {Promise<Array>} List of preset templates
   */
  async fetchPresets() {
    const url = `${API_BASE}/presets`;
    const res = await fetch(url, {
      headers: await this.getHeaders(url, "GET")
    });
    const data = await res.json();
    return data.data;
  },

  async fetchOptions(avatarType) {
    const url = avatarType ? `${API_BASE}/options/${avatarType}` : `${API_BASE}/options`;
    const res = await fetch(url, {
      headers: await this.getHeaders(url, "GET")
    });
    const data = await res.json();

    // Helper to resolve icon URLs through the proxy
    const resolveIconUrl = icon => {
      if (!icon) return icon;
      const url = icon.startsWith("http") ? icon : `${API_BASE}/${icon}`;
      return proxiedUrlFor(url);
    };

    // If avatarType-specific, data.data is an object with accessory arrays
    if (avatarType) {
      const options = data.data;
      // Resolve bodyShapePresets icon URLs
      if (options.bodyShapePresets) {
        options.bodyShapePresets = options.bodyShapePresets.map(preset => ({
          ...preset,
          icon: resolveIconUrl(preset.icon)
        }));
      }
      return options;
    }

    // If all genders, data.data is an object with male/female keys
    return data.data;
  },

  async fetchAssets(avatarType, type) {
    const params = new URLSearchParams();
    if (avatarType) params.set("avatarType", avatarType);
    if (type) params.set("type", type);
    const url = `${API_BASE}/assets?${params}`;
    const res = await fetch(url, {
      headers: await this.getHeaders(url, "GET")
    });
    const data = await res.json();
    return data.data;
  },

  /**
   * Fetch category definitions
   * @param {string} avatarType
   * @returns {Promise<Array>} Categories
   */
  async fetchCategories(avatarType) {
    const url = `${API_BASE}/categories/${avatarType}`;
    const res = await fetch(url, {
      headers: await this.getHeaders(url, "GET")
    });
    const data = await res.json();

    const resolveIconUrl = icon => {
      if (!icon) return icon;
      const url = icon.startsWith("http") ? icon : `${API_BASE}/${icon}`;
      return proxiedUrlFor(url);
    };

    return data.data.map(cat => ({
      ...cat,
      icon: resolveIconUrl(cat.icon),
      subcategories: cat.subcategories?.map(sub => ({
        ...sub,
        icon: resolveIconUrl(sub.icon)
      }))
    }));
  },

  // Returns blob for now, will switch to URL when persistence saves generated avatars
  async generateAvatar(descriptor) {
    if (this._generateController) {
      this._generateController.abort();
    }
    this._generateController = new AbortController();
    const signal = this._generateController.signal;

    try {
      const url = `${API_BASE}/generate`;
      const res = await fetch(url, {
        method: "POST",
        headers: await this.getHeaders(url, "POST"),
        body: JSON.stringify(descriptor),
        signal
      });
      if (!res.ok) throw new Error("Failed to generate avatar");
      return res.blob();
    } finally {
      if (this._generateController && this._generateController.signal === signal) {
        this._generateController = null;
      }
    }
  },

  // Generate a random descriptor from available options for an avatar type
  async getRandomDescriptor(avatarType) {
    const options = await this.fetchOptions(avatarType);

    const pickRandom = arr => (arr && arr.length > 0 ? arr[Math.floor(Math.random() * arr.length)] : null);
    const excludedRandomKeys = new Set(["overall", "outfit"]);

    // Dynamically pick a random asset for every category present in options
    const randomDescriptor = { avatarType };
    for (const [key, items] of Object.entries(options)) {
      if (excludedRandomKeys.has(key)) continue;
      if (Array.isArray(items) && items.length > 0) {
        randomDescriptor[key] = pickRandom(items)?.id;
      }
    }

    // Ensure consistent body type combinations (Skinny, Muscular, Fat)
    const bodyType = Math.floor(Math.random() * 3);
    if (bodyType === 0) {
      // Skinny (average)
      randomDescriptor.bodyWeight = 0;
      randomDescriptor.muscle = 0;
    } else if (bodyType === 1) {
      // Muscular (athletic)
      randomDescriptor.bodyWeight = 0;
      randomDescriptor.muscle = 1;
    } else {
      // Fat (plussize)
      randomDescriptor.bodyWeight = 1;
      randomDescriptor.muscle = 0;
    }

    return randomDescriptor;
  }
};
